import React, { useState, useEffect, useCallback } from 'react';
import './ThemeSelection.css';
import { useTranslation } from '../utils/languageUtils';
import { BiArrowBack } from 'react-icons/bi';

const isDev = process.env.NODE_ENV === 'development';

const ThemeSelection = ({
  themes,
  onThemeSelect,
  onClose,
  isTestMode = false,
  selectedThemeIndex = null,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themePreviews, setThemePreviews] = useState([]);

  // Check if we're in kiosk mode
  const isKioskMode = typeof window !== 'undefined' && 
    window.location && 
    window.location.search && 
    window.location.search.includes('kiosk=true');

  // Note: localStorage clearing is handled globally in App.js on mount

  // Process themes into previews without any Tab related logic
  const processThemes = useCallback(() => {
    if (!themes || themes.length === 0) return [];

    return themes.reduce((acc, theme, index) => {
      let stickerCount = 0;
      let backgroundCount = 0;

      if (Array.isArray(theme.StickersCsv)) {
        stickerCount += theme.StickersCsv.length;
      }
      if (Array.isArray(theme.BackgroundsCsv)) {
        backgroundCount += theme.BackgroundsCsv.length;
      }

      if (stickerCount + backgroundCount === 0) {
        if (isDev) {
          console.warn(`Theme ${theme.ThemeName} has no stickers or backgrounds, skipping`);
        }
        return acc; // skip themes without sticker or background sources
      }

      acc.push({
        theme,
        themeIndex: index,
        coverImage: theme.ThemeCover || null,
        stickerCount,
        backgroundCount,
      });
      return acc;
    }, []);
  }, [themes]);

  // Load previews and pre-select theme if selectedThemeIndex is provided
  useEffect(() => {
    setLoading(true);
    const previews = processThemes();
    setThemePreviews(previews);

    if (
      selectedThemeIndex !== null &&
      previews[selectedThemeIndex] &&
      previews[selectedThemeIndex].theme
    ) {
      setSelectedTheme(previews[selectedThemeIndex].theme);
    } else {
      setSelectedTheme(null);
    }
    setLoading(false);
  }, [themes, processThemes, selectedThemeIndex]);

  // Handle theme click selection
  const handleThemeClick = (themeIndex) => {
    const themeData = themePreviews[themeIndex];
    if (!themeData) return;
    setSelectedTheme(themeData.theme);
    if (onThemeSelect) {
      onThemeSelect(themeData.theme);
    }
    if (onClose) {
      onClose();
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  // Handle back button click in kiosk mode
  const handleKioskBack = () => {
    if (window.kioskHandler) {
      window.kioskHandler.handleKioskBack();
    } else if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="theme-selection-overlay">
        <div className="theme-selection-modal">
          {isKioskMode ? (
            <div className="kiosk-header">
              <img src="/kiosk/Header.png" alt="Header" className="kiosk-header-bg" />
              <div className="kiosk-header-overlay">
                <div className="kiosk-header-right">
                  <img src="/kiosk/Back.png" alt="Back" className="kiosk-back-btn" onClick={handleKioskBack} />
                  <img src="/kiosk/Language.png" alt="Language" className="kiosk-lang-btn" />
                  <img
                    src="/kiosk/Home.png"
                    alt="Home"
                    className="kiosk-home-btn"
                    onClick={() => {
                      if (window.kioskHandler && typeof window.kioskHandler.enterAttractMode === 'function') {
                        window.kioskHandler.enterAttractMode();
                        return;
                      }
                      window.location.href = '/';
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="theme-selection-header">
              <h2>{t('general.loading')}</h2>
            </div>
          )}
          <div className="theme-loading">
            <div className="loading-spinner"></div>
            <p>{t('stickers.loadingThemes')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-selection-overlay" onClick={handleCancel} role="presentation">
      <div className="theme-selection-modal" onClick={(e) => e.stopPropagation()}>
        {isKioskMode ? (
          <div className="kiosk-header">
            <img src="/kiosk/Header.png" alt="Header" className="kiosk-header-bg" />
            <div className="kiosk-header-overlay">
              <div className="kiosk-header-right">
                <img src="/kiosk/Back.png" alt="Back" className="kiosk-back-btn" onClick={handleKioskBack} />
                <img src="/kiosk/Language.png" alt="Language" className="kiosk-lang-btn" />
                <img
                  src="/kiosk/Home.png"
                  alt="Home"
                  className="kiosk-home-btn"
                  onClick={() => {
                    if (window.kioskHandler && typeof window.kioskHandler.enterAttractMode === 'function') {
                      window.kioskHandler.enterAttractMode();
                      return;
                    }
                    window.location.href = '/';
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="theme-selection-header">
            <h2>{t('theme.title')}</h2>
            <p className="theme-selection-subtitle">
              {isTestMode ? t('theme.chooseOptional') : t('theme.subtitle')}
            </p>
          </div>
        )}
        <div className="theme-grid">
          {themePreviews.length === 0 ? (
            <div className="no-themes">
              <p>{t('theme.noThemes')}</p>
            </div>
          ) : (
            themePreviews.map((themeData, index) => (
              <div
                key={index}
                className={`theme-card theme-level ${
                  selectedTheme === themeData.theme ? 'selected' : ''
                }`}
                onClick={() => handleThemeClick(index)}
                tabIndex={0}
                role="button"
                aria-pressed={selectedTheme === themeData.theme}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleThemeClick(index);
                  }
                }}
              >
                {themeData.coverImage ? (
                  <div className="theme-cover-container">
                    <img
                      src={themeData.coverImage}
                      alt={`${themeData.theme.ThemeName} cover`}
                      className="theme-cover-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="theme-image-placeholder" style={{ display: 'none' }}>
                      <span>{themeData.theme.ThemeName}</span>
                    </div>
                  </div>
                ) : (
                  <div className="theme-info">
                    <h3 className="theme-name">{themeData.theme.ThemeName}</h3>
                    <p className="sticker-count">
                      {themeData.stickerCount + themeData.backgroundCount} {t('theme.stickerSets')}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemeSelection;
